import React from 'react';
import styled from 'styled-components';
import Button from '@/se/components/Button[DEPRECATED]';
import type { RetainedBeaconChoice, RetainedBeaconEligibility } from '@/hooks/useRetainedBeaconDischarge';
import { useScope } from '@/hooks/useScope';
import { formatRetainedScheduledStart } from '@/components/entities/patient/views/RetainedBeaconDischargeChoice';
import { Modal, Subtitle, Title } from '../../../tablet/Modal';

interface Props {
  eligibility?: RetainedBeaconEligibility | null;
  onChoose: (choice: RetainedBeaconChoice) => void | Promise<void>;
  onCancel: () => void;
  working?: boolean;
}

/**
 * Full-screen retained-beacon confirmation for the PACU/Pre-Op/Post-Op kiosk.
 * It deliberately uses the existing tablet modal primitives instead of the
 * desktop Material UI dialog so its sizing and interaction match the kiosk.
 */
export function RetainedBeaconDischargeConfirmModal({
  eligibility,
  onChoose,
  onCancel,
  working = false,
}: Props) {
  const scope = useScope();
  const hospitalTimeZone = scope?.hospital?.timezone?.id ?? 'UTC';
  const scheduledStart = formatRetainedScheduledStart(eligibility?.scheduledStart, hospitalTimeZone);

  return (
    <Modal
      onClick={event => {
        event.preventDefault();
        event.stopPropagation();
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="retained-beacon-kiosk-title"
    >
      <Title id="retained-beacon-kiosk-title">Confirm Discharge</Title>
      <Prompt prominent>Are you sure you want to discharge this patient?</Prompt>
      {scheduledStart && <Details>Next scheduled procedure: {scheduledStart}</Details>}
      <Guidance>
        Choose “Discharge and keep beacon” only when the patient will keep the beacon while remaining in the hospital.
      </Guidance>
      <Actions>
        <Button xl onClick={onCancel} disabled={working}>
          Cancel
        </Button>
        <Button xl onClick={() => onChoose('standard')} disabled={working}>
          Standard discharge
        </Button>
        <Button xl primary onClick={() => onChoose('retain')} disabled={working}>
          Discharge and keep beacon
        </Button>
      </Actions>
    </Modal>
  );
}

const Prompt = styled(Subtitle)`
  && {
    margin: 0 0 1rem;
    font-size: 1.75rem;
  }
`;

const Details = styled.p`
  margin: 0 0 0.75rem;
  font-size: 1.5rem;
  line-height: 1.5;
`;

const Guidance = styled.p`
  max-width: 48rem;
  margin: 0 0 2.5rem;
  font-size: 1.35rem;
  line-height: 1.5;
  opacity: 0.75;
`;

const Actions = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1rem;
  width: min(100%, 66rem);

  > button {
    width: 100%;
    min-height: 4.75rem;
    padding-left: 1rem;
    padding-right: 1rem;
  }

  @media (max-width: 50rem) {
    grid-template-columns: 1fr;
    max-width: 28rem;
  }
`;
